import { Vector3 } from 'three'
import type { RoadSpline } from '../core/spline/splineGenerator'
import { SampledPath } from './sampledPath'

/**
 * The curve a full bus drives away on — port of
 * `BusSplineFollower.CreateExitSpline`, built by `DepartAndExit`.
 *
 * A departing bus does **not** keep circling. `DepartAndExit` swaps
 * `_currentSpline` for a brand-new throwaway spline and repositions the bus at
 * its start, so from that moment the bus is off the road entirely. That is the
 * behaviour this port was missing: buses stayed on the loop with an
 * `isDeparting` flag, and since `IsSpaceClearForBus` skips departing buses, a
 * merging bus could be admitted straight on top of one.
 *
 * Shape of the curve, from the decompile:
 *
 *   - optionally one lead-in point, when backing up by `leadIn` would run off
 *     the start of an open spline;
 *   - **12 points** along the road, `start + span * i / 11` for i in 0..11,
 *     where `start` is the head backed up by `leadIn` and `span` reaches the
 *     bus's tail — so the curve still covers the stretch the body is standing on;
 *   - **6 points** running straight out along the road's tangent at the exit
 *     point, at offsets from a static float array.
 *
 * Then `SetPoints` + `RebuildImmediate`, same as the entry curve.
 */

/** The road-sampled section is always 12 points, `i / 11` across the span. */
const ROAD_SECTION_POINTS = 12

/**
 * The straight run-off: 6 points along the tangent at the exit point.
 *
 * The count is recovered — `FUN_00ab1834(type, 6)` before `InitializeArray` —
 * but the six offsets themselves live in a static data blob the decompile does
 * not spell out, and `bustraffic_kit` ships no binary and no `global-metadata`
 * to read them out of. That matters less than it looks: the points are
 * **collinear**, all on the same tangent ray, so their spacing cannot change
 * the curve's shape. Only the total does anything, by setting how far off the
 * road the bus drives before `LateUpdate` reaches `0.999` and
 * `Bus.CompleteOffscreen` destroys it outright (`__0x20fbfc0.c` — a bare
 * `Object.Destroy`, no fade, no tween: the bus pops).
 *
 * 📐 **The total, recovered from the level data.** This was a bare PLACEHOLDER
 * for a long time. It is still not read from the original, but it is no longer
 * a number picked to look right: `npm run check:runoff` sweeps candidates over
 * all forty levels and 597 departures and 30 is the **smallest** at which both
 * measurable defects are at their floor.
 *
 *     L    off-road ray on camera   destroyed on camera   destroyed on the road
 *     16                     1333             597/597                 276/597
 *     20                     2870             597/597                  47/597
 *     22                     3429             596/597                   0/597
 *     26                     3601             531/597                   0/597
 *     30                     3645             275/597                   0/597   <- shipped
 *     60                     3975             275/597                   0/597
 *
 * Below 22 a bus is destroyed **while still standing on the tarmac** — it
 * vanishes on the road in front of the player, which a shipped game does not
 * do. Above 30 nothing improves: 275 of 597 departures are destroyed on camera
 * whatever the length, because on those levels the ray never leaves the frame
 * at all, and every extra unit is more bus drawn off the road.
 *
 * ❓ **What this does NOT say is that the original used 30.** It says 30 is the
 * unique smallest value meeting two criteria derived from the shipped level
 * data and camera. Two other derivations were tried and are **ruled out by
 * measurement**, which is worth more than the value itself:
 *
 *  - *"long enough to leave the frame"*, from the name `CompleteOffscreen` —
 *    needs up to **52.5** units, and on **level 26 no finite length works**;
 *  - *"short enough to stay on the tarmac"* — needs less than **6.3** units.
 *
 * They disagree by a factor of eight, so neither is the rule the original used,
 * and no constant satisfies both. Logged in `GAMEPLAY-CHECKLIST.md` §10.
 *
 * ❓ **"Tune against the clip" was tried, and the clip cannot answer it.** With
 * `npm run ref` in hand the plan was to watch a full bus leave and see how far
 * it runs before it is removed. The bus that looks like the answer is not one:
 * tracked from t = 9.4 s to 14.2 s, the purple bus with passengers aboard rounds
 * the bottom bend **following the road** and then sits there barely moving. A
 * departing bus can do neither — `LateUpdate`'s departing branch pushes
 * `_headPercent` along its own exit spline at a flat `_worldSpeed * 1.68` with
 * no traffic gate at all. So that bus is a **partly-loaded** one released by
 * `ReleaseHeldBus` and lapping again, which looks identical from above once the
 * platform is out of frame.
 *
 * What the clip would need to settle it is a bus caught between filling and
 * vanishing, in frame, unobscured. Thirty seconds of level 8 does not contain
 * one that is legible at 332x720.
 */
const RUN_OFF_POINTS = 6
export const RUN_OFF_LENGTH = 30

export class ExitPath extends SampledPath {
  /**
   * Arc distance at which the curve leaves the road and the run-off begins.
   * The bus's head starts here, so its body covers the road section behind it.
   */
  readonly roadSectionLength: number

  constructor(controlPoints: Vector3[]) {
    super(controlPoints)
    this.roadSectionLength = this.cumulative[
      this.sampleIndexOfControlPoint(ROAD_SECTION_POINTS - 1)
    ]
  }
}

/**
 * @param headDistance arc distance of the bus's head along the road
 * @param tailDistance arc distance of its tail
 * @param leadIn how far behind the head to start the curve — `DepartAndExit`
 *   passes the bus's head extension plus its min gap plus 2
 */
export function createExitSpline(
  road: RoadSpline,
  headDistance: number,
  tailDistance: number,
  leadIn: number,
): ExitPath {
  const { spline } = road
  const loop = spline.length
  const wrap = (d: number) => ((d % loop) + loop) % loop

  // The road section runs from behind the rear bumper forward to the front one,
  // so the body is standing on it and the run-off continues in the direction of
  // travel. Straight out of `CreateExitSpline`, lines 106-114:
  //
  //     start = TravelBackwardWrapped(_tailPercent, leadIn)
  //     span  = _headPercent - start
  //
  // — the same two lines written below, under the same two names. An earlier
  // version of this comment claimed the original's head and tail were the
  // reverse of this port's; that was a misread of which offset is which
  // (0x88 is `_headPercent`, 0x90 is `_tailPercent`, `dump.cs` 710495). The
  // code here never changed, only the story about it.
  const start = wrap(tailDistance - Math.max(0, leadIn))
  const span = wrap(headDistance - start)

  const control: Vector3[] = []
  for (let i = 0; i < ROAD_SECTION_POINTS; i++) {
    const distance = wrap(start + (span * i) / (ROAD_SECTION_POINTS - 1))
    control.push(spline.evaluatePosition(spline.percentAtDistance(distance)))
  }

  // Straight out along the tangent where the bus leaves.
  const exitPercent = spline.percentAtDistance(wrap(headDistance))
  const exitPoint = spline.evaluatePosition(exitPercent)
  const tangent = spline.evaluateTangent(exitPercent)
  tangent.y = 0
  if (tangent.lengthSq() < 1e-9) tangent.set(0, 0, 1)
  tangent.normalize()

  for (let i = 1; i <= RUN_OFF_POINTS; i++) {
    control.push(exitPoint.clone().addScaledVector(tangent, (RUN_OFF_LENGTH * i) / RUN_OFF_POINTS))
  }

  return new ExitPath(control)
}
